import { merge } from 'lodash-es';
import { type PropsWithChildren } from 'react';

import { DEFAULT_DATATABLE_V2_CONFIG } from '../DataTableV2/constants.js';
import type {
  DataTableV2Config,
  DataTableV2Props,
} from '../DataTableV2/public.api.js';

/**
 * Build DataTableV2 configuration.
 *
 * @param props - config from props
 * @param config - configuration to use
 *
 * @returns DataTableV2Config
 * @internal
 */
export function buildDataTableV2Config(
  props: PropsWithChildren<DataTableV2Props>,
  config: DataTableV2Config,
): DataTableV2Config {
  // TODO: remove the whole function, keep in features
  const columnVisibility =
    props.columnVisibility ?? config.columnVisibility ?? {};
  const fullWidth =
    props.fullWidth ??
    config.fullWidth ??
    DEFAULT_DATATABLE_V2_CONFIG.fullWidth;
  const lineWrap =
    props.lineWrap ?? config.lineWrap ?? DEFAULT_DATATABLE_V2_CONFIG.lineWrap;
  const resizable =
    props.resizable ??
    config.resizable ??
    DEFAULT_DATATABLE_V2_CONFIG.resizable;
  const sortable =
    props.sortable ?? config.sortable ?? DEFAULT_DATATABLE_V2_CONFIG.sortable;
  const pagination =
    config.pagination ?? DEFAULT_DATATABLE_V2_CONFIG.pagination;
  const selectableRows =
    props.selectableRows ??
    config.selectableRows ??
    DEFAULT_DATATABLE_V2_CONFIG.selectableRows;
  const rowThresholds =
    props.rowThresholds ??
    config.rowThresholds ??
    DEFAULT_DATATABLE_V2_CONFIG.rowThresholds;
  const variant =
    props.variant ?? config.variant ?? DEFAULT_DATATABLE_V2_CONFIG.variant;

  return {
    ...props,
    columnVisibility,
    selectableRows,
    fullWidth,
    lineWrap,
    resizable,
    sortable,
    pagination,
    variant,
    rowThresholds,
  };
}

/**
 * Gets DataTableV2 configuration, merging the default ones with the stringified json config.
 * For the case that there is a conflict property, default would prevail.
 * For the case that there is any parse error, it will return an empty config.
 *
 * @param serializedConfig - stringify json config
 *
 * @returns - DataTableV2Config
 */
export function deserializeDataTableV2Config(
  serializedConfig: string,
): DataTableV2Config {
  try {
    return merge({}, DEFAULT_DATATABLE_V2_CONFIG, JSON.parse(serializedConfig));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return DEFAULT_DATATABLE_V2_CONFIG;
  }
}
